import { array as codecArray } from 'purify-ts/Codec';
import { makeResponses } from '../response_factory';
import { utils as coreUtils, paginated, requestParams } from '@codegrade/api-core';
import { EnterRestrictionData, EntryOverviewEntry, HeartbeatResponse, IPRange, PatchEntryOverrideRestrictionData, PatchRestrictionData, Restriction, UserLoginResponse, } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
const makeResult = coreUtils.makeResult;
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function restriction(config) {
    const deleteAllowedIpResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const putAllowedIpResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(IPRange)),
    });
    const enterResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(UserLoginResponse)),
    });
    const getAllEntriesResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(EntryOverviewEntry))),
    });
    const getAllAllowedIpsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(IPRange))),
    });
    const getResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(Restriction)),
    });
    const patchResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(Restriction)),
    });
    const patchEntryOverrideResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(EntryOverviewEntry)),
    });
    const heartbeatResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(HeartbeatResponse)),
    });
    return Object.freeze({
        /**
         * Remove a single IP range from a restriction.
         *
         * @param arg The arguments to the function.
         * @param arg.restrictionId The ID of the restriction.
         * @param arg.ipRange The IP range to remove.
         * @returns An empty response.
         */
        deleteAllowedIp: async ({ restrictionId, ipRange, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/restrictions/${restrictionId}/allowed_ips/${ipRange}`, deleteAllowedIpResponse.v, undefined);
            return result;
        },
        /**
         * Add a single IP range to a restriction.
         *
         * @param arg The arguments to the function.
         * @param arg.restrictionId The id of the restriction.
         * @param arg.ipRange The IP range to add in CIDR notation.
         * @returns The added IP range.
         */
        putAllowedIp: async ({ restrictionId, ipRange, }) => {
            const result = await requestParams(config, 'PUT', `/api/v1/restrictions/${restrictionId}/allowed_ips/${ipRange}`, putAllowedIpResponse.v, undefined);
            return result;
        },
        /**
         * Enter a restricted course, recording an entry event.
         *
         * Validates the password (if set), enforces the entry limit (if session_lockdown is set), records a :class:.RestrictionEntryEvent, and returns the caller's existing access token enriched with any new restrictions. The same token is reused so the student is not locked out if the response is lost in transit.
         *
         * @param arg The arguments to the function.
         * @param arg.restrictionId The ID of the restriction.
         * @param arg.requestBody The body of the request.
         * @returns A login response containing the (updated) access token.
         */
        enter: async ({ restrictionId, requestBody, }) => {
            const result = await requestParams(config, 'PUT', `/api/v1/restrictions/${restrictionId}/entries/`, enterResponse.v, JSON.stringify(EnterRestrictionData.encode(requestBody)));
            return result;
        },
        /**
         * List all enrolled students with their entry counts and overrides.
         *
         * @param arg The arguments to the function.
         * @param arg.restrictionId The ID of the restriction.
         * @param arg.q Only retrieve students whose name or username matches this value.
         * @param arg.pageSize The size of a single page, maximum is 100.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns A paginated list of entry overview entries per student.
         */
        getAllEntries: async ({ restrictionId, q = undefined, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/restrictions/${restrictionId}/entries/`, getAllEntriesResponse.v, undefined, {
                q,
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Get all IP ranges for a restriction.
         *
         * @param arg The arguments to the function.
         * @param arg.restrictionId The ID of the restriction.
         * @param arg.pageSize The size of a single page, maximum is 100.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns A paginated list of IP ranges.
         */
        getAllAllowedIps: async ({ restrictionId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/restrictions/${restrictionId}/allowed_ips/`, getAllAllowedIpsResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Get the full settings for a restriction (admin only).
         *
         * @param arg The arguments to the function.
         * @param arg.restrictionId The id of the restriction.
         * @returns The restriction object including the password.
         */
        get: async ({ restrictionId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/restrictions/${restrictionId}`, getResponse.v, undefined);
            return result;
        },
        /**
         * Update restriction settings.
         *
         * The request body mirrors the restriction JSON shape. All fields are optional; omitted fields are left unchanged.
         *
         * @param arg The arguments to the function.
         * @param arg.restrictionId The id of the restriction.
         * @param arg.requestBody The body of the request.
         * @returns The updated restriction.
         */
        patch: async ({ restrictionId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/restrictions/${restrictionId}`, patchResponse.v, JSON.stringify(PatchRestrictionData.encode(requestBody)));
            return result;
        },
        /**
         * Set or remove a per-student entry override for a restriction.
         *
         * Sending `null` removes the override row entirely (the student falls back to the global `session_lockdown` limit). Sending a positive integer creates or updates the override for this student.
         *
         * @param arg The arguments to the function.
         * @param arg.restrictionId The ID of the restriction.
         * @param arg.userId The ID of the student.
         * @param arg.requestBody The body of the request.
         * @returns The updated entry overview for this student.
         */
        patchEntryOverride: async ({ restrictionId, userId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/restrictions/${restrictionId}/entries/${userId}`, patchEntryOverrideResponse.v, JSON.stringify(PatchEntryOverrideRestrictionData.encode(requestBody)));
            return result;
        },
        /**
         * Send a heartbeat to keep an isolated exam session alive.
         *
         * When heartbeat enforcement is active, the session's `expires_at` is set to a short window (5 minutes). This endpoint extends it forward, capped by `max_expires_at`.
         *
         * If the session is already expired, the auth flow rejects the request before it reaches this endpoint (401).
         *
         * @returns The session's `expires_at` after the refresh.
         */
        heartbeat: async () => {
            const result = await requestParams(config, 'POST', `/api/v1/restrictions/heartbeat`, heartbeatResponse.v, undefined);
            return result;
        },
    });
}
